import type { CallerIdentity } from "../../api/gateway";
import { priceInForce } from "../../api/planDetail";
import { listPriceComponents, listPriceRules, listSellerGst, priceWords, sellerWarningOf, speaksGst, warningWords, type PriceComponent, type PriceRule, type SellerGst } from "../../api/priceList";
import type { ReportRow } from "../../api/reports";

/**
 * Reports › All plans and Plans › View / edit plan, "Price" (owner 2026-10-29: "10Mb plan shows
 * ₹25 in component and price list, but All plans doesn't show price of any
 * plan"). The price left view_plan.group_desc (SQL 289), and it never goes back
 * there: it is read from billing, the same source as Plans › Price list.
 */
export const PRICE_COLUMN = "Price";

export type BillingByNamespace = Record<string, { components: PriceComponent[]; rules: PriceRule[]; seller: SellerGst } | { error: string }>;

/** Each namespace's price items and rules; one namespace failing never hides the others. */
export async function loadBilling(identity: CallerIdentity, namespaces: string[], signal?: AbortSignal): Promise<BillingByNamespace> {
  const out: BillingByNamespace = {};
  await Promise.all(
    namespaces.map(async (ns) => {
      try {
        const [components, rules, seller] = await Promise.all([
          listPriceComponents(identity, ns, signal),
          listPriceRules(identity, ns, signal),
          listSellerGst(identity, ns, signal),
        ]);
        out[ns] = { components, rules, seller };
      } catch (e) {
        out[ns] = { error: (e as Error).message };
      }
    }),
  );
  return out;
}

export function rowNamespaces(rows: ReportRow[]): string[] {
  return [...new Set(rows.map((r) => (typeof r.namespace === "string" ? r.namespace : "")).filter(Boolean))].sort();
}

const text = (v: unknown) => (typeof v === "string" ? v : "");

/**
 * The rows with the price column after the plan's heading. Pure. Until billing
 * answers the cell says so; a plan billing cannot price says why, never ₹0.
 */
export function withPlanPrices(rows: ReportRow[], billing: BillingByNamespace | undefined, now: Date = new Date()): ReportRow[] {
  return rows.map((r) => {
    const cell = planPriceCell(r, billing, now);
    const out: ReportRow = {};
    let placed = false;
    for (const [k, v] of Object.entries(r)) {
      out[k] = v;
      if (k === "group_hd") {
        out[PRICE_COLUMN] = cell;
        placed = true;
      }
    }
    if (!placed) out[PRICE_COLUMN] = cell;
    return out;
  });
}

/** One plan's price cell: the price as the owner words it, or why there is none. */
export function planPriceCell(
  r: { namespace?: unknown; groupuid?: unknown; groupname?: unknown },
  billing: BillingByNamespace | undefined,
  now: Date = new Date(),
): string {
  const b = billing?.[text(r.namespace)];
  if (!billing) return "…";
  if (!b) return "not loaded";
  if ("error" in b) return `not loaded: ${b.error}`;
  const p = priceInForce({ groupuid: text(r.groupuid), groupname: text(r.groupname) }, b.components, b.rules, now, b.seller);
  const speak = speaksGst(b.seller);
  if ("gross" in p) {
    const w = speak ? sellerWarningOf({ id: p.componentId }, b.seller) : undefined;
    return w ? `${priceWords(p.gross, p.gst)} ⚠ ${warningWords(w)}` : priceWords(p.gross, p.gst, speak);
  }
  if (!speak && p.problem === "GST not known") return "— (price not available)";
  if (p.problem === "several prices in force") {
    console.warn(`Plan price: ${text(r.groupname)} has several prices in force in ${text(r.namespace)}`);
    return "— (several prices in force; see Price list)";
  }
  return `— (${p.problem})`;
}
